import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SiteLanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
});

const SUPPORTED_LANGUAGES = ['en', 'el'];
const STORAGE_KEY = 'site_language';

function stripElPrefix(pathname) {
  if (pathname === '/el') return '/';
  if (pathname.startsWith('/el/')) return pathname.slice(3);
  return pathname;
}

function isLocalizablePath(pathname) {
  const plain = stripElPrefix(pathname);
  return /^\/($|features(?:\/[^/]+)?$|use-cases(?:\/[^/]+)?$|contact$|faq$|pricing$|login$|register$|forgot-password$|reset-password$|privacy$|terms$|cookies$|payment-options$)/.test(plain);
}

function pathForLanguage(pathname, language) {
  const plain = stripElPrefix(pathname);
  if (!isLocalizablePath(pathname)) return pathname;
  if (language === 'el') return plain === '/' ? '/el' : `/el${plain}`;
  return plain;
}

export function SiteLanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [language, setLanguageState] = useState(() => {
    const fromPath = window.location.pathname === '/el' || window.location.pathname.startsWith('/el/')
      ? 'el'
      : null;
    if (fromPath) return fromPath;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (SUPPORTED_LANGUAGES.includes(stored)) return stored;
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (!isLocalizablePath(location.pathname)) return;
    const targetPath = pathForLanguage(location.pathname, language);
    if (targetPath !== location.pathname) {
      navigate(`${targetPath}${location.search}${location.hash}`, { replace: true });
    }
  }, [language, location.pathname, location.search, location.hash, navigate]);

  const setLanguage = useCallback((next) => {
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    setLanguageState(next);
  }, [setLanguageState]);

  const value = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return (
    <SiteLanguageContext.Provider value={value}>
      {children}
    </SiteLanguageContext.Provider>
  );
}

export function useSiteLanguage() {
  return useContext(SiteLanguageContext);
}
